import { CrownOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Dropdown, Menu, Popconfirm, Typography } from 'antd'
import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserAvatar } from '@/components'
import { userInfoAtom, usersAtom } from '@/modules/user'
import { ResourceType } from '@/schemas'

import { AddUser } from './add-user.component'

const { Title } = Typography

export const CommitteeMemberManager: React.FC<{ resourceType: ResourceType }> = ({ resourceType }) => {
  const { t } = useTranslation()

  const [users] = useAtom(usersAtom)
  const [viewer] = useAtom(userInfoAtom)
  const isSuperAdmin = viewer.isSuperAdmin
  const hasDeletePermission = viewer.roles.find((r) => r.role === `${resourceType}_committee`) || isSuperAdmin
  const { run: getPermission, data: members } = useRequest<string[]>(
    () => ({
      url: '/api/permissions/committee/permission',
      method: 'get',
      params: { resourceType },
    }),
    {
      manual: true,
    }
  )
  const { run: deletePermission } = useRequest<string[]>(
    (email: string) => ({
      url: '/api/permissions/committee/permission',
      method: 'delete',
      params: { email, resourceType },
    }),
    {
      manual: true,
      onSuccess: (_, [email]) => {
        getPermission()
        if (viewer.email === email) {
          window.location.reload()
        }
      },
    }
  )

  React.useEffect(() => {
    getPermission()
  }, [])

  const onAddSuccess = (email?: string) => {
    if (viewer.email === email) {
      window.location.reload()
    }
    getPermission()
  }

  const menu = (email: string) => {
    const userName = users.find((user) => user.email === email)?.name
    return (
      <Menu>
        <Menu.Item key='delete' disabled={!hasDeletePermission} icon={<DeleteOutlined />}>
          {hasDeletePermission ? (
            <Popconfirm
              placement='top'
              title={t('committeeDeleteConfirm', { userName })}
              onConfirm={() => deletePermission(email)}
              okText={t('remove')}
              cancelText={t('cancel')}
            >
              {t('remove') + ': ' + userName}
            </Popconfirm>
          ) : (
            <span>{t('remove') + ': ' + userName}</span>
          )}
        </Menu.Item>
      </Menu>
    )
  }

  return (
    <div>
      <Title level={4}>
        <TeamOutlined />
        <span className='pl-1'>{t('committeeMembersTitle')}</span>
      </Title>
      <div className='flex mt-2 items-center'>
        <AddUser onSuccess={onAddSuccess} resourceType={resourceType} />
        <div className='flex flex-nowrap py-1 pb-3 items-center overflow-x-auto'>
          {members?.map((email) => (
            <div key={email}>
              <Dropdown placement='topCenter' overlay={menu(email)} trigger={['click']} disabled={false}>
                <div className='cursor-pointer flex'>
                  <UserAvatar email={email} size={'small'} />
                  <div className='ml-2 ' style={{ fontSize: '12px' }}>
                    <div className='my-0 w-20 whitespace-nowrap overflow-hidden' style={{ textOverflow: 'ellipsis' }}>
                      <span className='pr-1'>{users.find((u) => u.email === email)?.name}</span>
                      <CrownOutlined />
                    </div>
                    {t('committeeMember')}
                  </div>
                </div>
              </Dropdown>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
